<script lang="ts">
    import { VpPlayIcon } from '@decathlon-experience/icons/svelte';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import Button from '$saga/components/Button.svelte';
    import Loader from '$saga/components/Loader.svelte';
    import { TOUR_TYPE_GIR, TYPEBOOKING_EBOOKING } from '$saga/constants/tour';
    import PaxAddRoomButton from '$saga/views/shared/RoomPax/PaxAddRoomButton.svelte';
    import PaxHiddenInputs from '$saga/views/shared/RoomPax/PaxHiddenInputs.svelte';
    import PaxRemainingStockNotice from '$saga/views/shared/RoomPax/PaxRemainingStockNotice.svelte';
    import PaxRoomCard from '$saga/views/shared/RoomPax/PaxRoomCard.svelte';
    import PaxStepIntro from '$saga/views/shared/RoomPax/PaxStepIntro.svelte';
    import PaxSummary from '$saga/views/shared/RoomPax/PaxSummary.svelte';
    import {
        alignAgesWithCount,
        areAgesValid,
        computeIsChildrenAllowed,
        computeIsPaxValid,
        computeMinTotalPax,
        computePaxLimits,
        getEffectiveRoomAdultMax,
        hasAgeBelowMinimum,
        hasAgeOutsideBounds,
        normalizePaxCount,
    } from '$saga/views/Tour/Aside/form/SelectPaxStepForm/utils';
    import { datesInfos } from '$saga/views/Tour/store';
    import type { AgeRules, FormValues } from '$saga/views/Tour/types';

    import TravellerAgeInputs from './TravellerAgeInputs.svelte';

    export let tourType: number;
    export let bookingType: string;
    export let isTravelAloneAuthorized: boolean;
    export let minAgeAllowed: number | null;
    export let maxAgeAllowed: number | null;
    export let formValues: FormValues;
    export let ageRules: AgeRules;
    export let showIndividualAges: boolean;
    export let showAdultAges: boolean;
    export let showChildAges: boolean;
    export let adultMinAge: number;
    export let adultMaxAge: number | null;
    export let childMinAge: number;
    export let childMaxAge: number | null;
    export let requiredPax: number | null = null;
    export let isLoading: boolean = false;

    const isEbookingType = bookingType === TYPEBOOKING_EBOOKING;
    const isGir = tourType === TOUR_TYPE_GIR;
    const isChildrenAllowed = computeIsChildrenAllowed(
        isEbookingType,
        minAgeAllowed,
        maxAgeAllowed,
    );
    const minTotalPax = computeMinTotalPax(isTravelAloneAuthorized);

    type Room = {
        adults: number;
        adultsAges: string[];
        children: number;
        childrenAges: string[];
    };
    type Occupancies = Room[];

    let occupancies: Occupancies = [
        {
            adults: Math.max(1, formValues.adults),
            adultsAges: Array.from(
                { length: Math.max(1, formValues.adults) },
                () => '',
            ),
            children: 0,
            childrenAges: [],
        },
    ];

    const addRoom = (): void => {
        occupancies = [
            ...occupancies,
            {
                adults: 1,
                adultsAges: [''],
                children: 0,
                childrenAges: [],
            },
        ];
    };

    const removeRoom = (index: number): void => {
        occupancies = occupancies.filter((_, roomIndex) => roomIndex !== index);
    };

    $: roomCount = occupancies.length;
    $: totalAdults = occupancies.reduce((sum, room) => sum + room.adults, 0);
    $: totalChildren = occupancies.reduce(
        (sum, room) => sum + room.children,
        0,
    );
    $: flatChildrenAges = occupancies.map((room) => room.childrenAges).flat();
    $: flatAdultsAges = occupancies.map((room) => room.adultsAges).flat();
    $: formValues.adults = totalAdults;
    $: formValues.children = totalChildren;
    $: formValues.adultsAges = flatAdultsAges;
    $: formValues.childrenAges = flatChildrenAges;

    $: totalAdultsError = !isTravelAloneAuthorized && totalAdults === 1;
    // Room-based form uses totalAdults/totalChildren for stock cap calculations
    $: ({
        minAdults: minAdultsRuleValue,
        maxAdults: maxAdultsRuleValue,
        minChildren: minChildrenRuleValue,
        maxChildren: maxChildrenRuleValue,
    } = computePaxLimits({
        isEbookingType,
        isGir,
        isChildrenAllowed,
        isTravelAloneAuthorized,
        ageRules,
        adults: totalAdults,
        children: totalChildren,
        selectedDate: formValues.selectedDate,
        remainingStock: $datesInfos?.remainingStock ?? null,
    }));
    $: roomAdultMax = getEffectiveRoomAdultMax(maxAdultsRuleValue);

    $: areAdultsAgeValid =
        !showAdultAges ||
        occupancies.every((room) =>
            areAgesValid(
                room.adultsAges,
                room.adults,
                adultMinAge,
                adultMaxAge,
            ),
        );

    $: areChildrenAgeValid =
        !showChildAges ||
        occupancies.every((room) =>
            areAgesValid(
                room.childrenAges,
                room.children,
                childMinAge,
                childMaxAge,
            ),
        );
    $: adultAgeBelowMinimum =
        showAdultAges &&
        occupancies.some((room) =>
            hasAgeBelowMinimum(room.adultsAges, room.adults, adultMinAge),
        );
    $: ageOutsideBounds =
        showAdultAges &&
        !adultAgeBelowMinimum &&
        occupancies.some((room) =>
            hasAgeOutsideBounds(
                room.adultsAges,
                room.adults,
                adultMinAge,
                adultMaxAge,
            ),
        );

    $: individualAgesValid =
        !showIndividualAges || (areAdultsAgeValid && areChildrenAgeValid);
    $: snowtrexPaxMismatch =
        requiredPax !== null && totalAdults + totalChildren !== requiredPax;

    let isValid = false;
    $: {
        if (individualAgesValid) {
            isValid = computeIsPaxValid({
                adults: totalAdults,
                children: totalChildren,
                minAdults: minAdultsRuleValue,
                maxAdults: maxAdultsRuleValue,
                minChildren: minChildrenRuleValue,
                maxChildren: maxChildrenRuleValue,
                minTotalPax,
                requiredPax,
            });
        } else {
            isValid = false;
        }
    }

    const updateRoomAdults = (room: Room, event: Event): void => {
        room.adults = normalizePaxCount(
            (event.target as HTMLInputElement).value,
            1,
            roomAdultMax,
        );
        room.adultsAges = alignAgesWithCount(room.adultsAges, room.adults);
        occupancies = [...occupancies];
    };

    const updateRoomChildren = (room: Room, event: Event): void => {
        room.children = normalizePaxCount(
            (event.target as HTMLInputElement).value,
            0,
            maxChildrenRuleValue,
        );
        room.childrenAges = alignAgesWithCount(
            room.childrenAges,
            room.children,
        );
        occupancies = [...occupancies];
    };
</script>

<div class="mb-xl flex flex-col gap-xl py-2xl">
    <PaxStepIntro />

    <PaxSummary
        {totalAdults}
        {totalChildren}
        showAdultsError={totalAdultsError}
    />

    {#each occupancies as room, index (index)}
        <PaxRoomCard
            {index}
            adultCount={room.adults}
            childCount={room.children}
            showChildren={isChildrenAllowed}
            canIncrementAdults={room.adults !== roomAdultMax &&
                totalAdults !== maxAdultsRuleValue}
            canIncrementChildren={maxChildrenRuleValue !== totalChildren}
            childrenMax={maxChildrenRuleValue}
            {minAgeAllowed}
            {maxAgeAllowed}
            {ageRules}
            onAdultsIncrement={() => {
                if (
                    room.adults < roomAdultMax &&
                    (maxAdultsRuleValue == null ||
                        maxAdultsRuleValue > totalAdults)
                ) {
                    room.adults = room.adults + 1;
                    room.adultsAges = [...room.adultsAges, ''];
                }
            }}
            onAdultsDecrement={() => {
                if (room.adults > 1) {
                    room.adults = room.adults - 1;
                    room.adultsAges = room.adultsAges.slice(0, -1);
                }
            }}
            onAdultsInput={(event) => updateRoomAdults(room, event)}
            onChildrenIncrement={() => {
                if (
                    maxChildrenRuleValue == null ||
                    maxChildrenRuleValue > totalChildren
                ) {
                    room.children = room.children + 1;
                    room.childrenAges = [...room.childrenAges, ''];
                }
            }}
            onChildrenDecrement={() => {
                if (room.children > 0) {
                    room.children = room.children - 1;
                    room.childrenAges = room.childrenAges.slice(0, -1);
                }
            }}
            onChildrenInput={(event) => updateRoomChildren(room, event)}
            showDelete={roomCount > 1}
            onDelete={() => removeRoom(index)}
        >
            {#if showIndividualAges}
                <TravellerAgeInputs
                    bind:adultsAges={room.adultsAges}
                    bind:childrenAges={room.childrenAges}
                    adultCount={room.adults}
                    childCount={room.children}
                    {showAdultAges}
                    {showChildAges}
                    {adultMinAge}
                    {childMinAge}
                    {childMaxAge}
                    idPrefix="room-{index}"
                />
            {/if}
        </PaxRoomCard>
    {/each}
    <PaxAddRoomButton
        {roomCount}
        canIncrement={totalAdults + totalChildren !==
            $datesInfos?.remainingStock}
        onAdd={addRoom}
    />
    {#if snowtrexPaxMismatch}
        <div class="text-sm text-status-negative">
            {trans('tour.aside_panel.pax_popover.snowtrex_pax_unavailable')}
        </div>
    {:else if ageOutsideBounds}
        <div class="text-sm text-status-negative">
            {trans('tour.aside_panel.pax_popover.age_unavailable')}
        </div>
    {/if}
    <PaxRemainingStockNotice remainingStock={$datesInfos?.remainingStock} />
</div>

<!-- Footer -->
<div
    class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s mt-auto"
>
    <Button
        type="submit"
        form="prefunnel"
        class={cn('w-full', !isValid && 'cursor-not-allowed opacity-[0.38]')}
        disabled={!isValid || isLoading}
        endIcon={!isLoading ? VpPlayIcon : undefined}
    >
        {#if isLoading}
            <Loader />
        {:else}
            {trans('common.continue')}
        {/if}
    </Button>
</div>

<PaxHiddenInputs {occupancies} {totalAdults} {totalChildren} />
